import React, { useState } from 'react'
import {
  IconSearch,
  IconUsers,
  IconHome,
  IconBell,
  IconBookmark,
  IconLayoutGrid,
  IconStack2,
  IconFolder,
  IconFlag,
  IconRobotFace,
  IconServer2,
  IconBrandTrello,
  IconPhoto
} from '@tabler/icons-react'
import { useSelector, useDispatch } from 'react-redux'
import { Role } from '../../utility/types'
import { staticAssetsPath } from '../../configs/constants'
import { Badge } from 'dsm-react-lib'
import { Icon } from '../../@core/layouts/components/navbar/NavbarUser'
import { useFeatureGate } from '../../_hooks/useFeatureGate'
import { useWorkspaceService } from '../../_services/WorkspaceService'
import {
  AIApps,
  HomeIcon,
  WorkspaceIcon2,
  TeamsIcon,
  AdminIcon,
  SmartInsightsIcon,
  DataProductIcon,
  SmartDataIcon,
  SmartFlowIcon,
  AllAppsIcon,
  ManageAgentIcon
} from '../../views/_pages/Chatbot/icons/Icons'
import { Navigate, useNavigate } from 'react-router-dom'
import { has } from 'lodash'
//@ts-ignore
import { Input, Button } from 'dsm-react-lib'
import { useSasToken } from '../../_hooks/useSasToken'
import { useThemeManager } from '../../_hooks/useThemeManager'
import { defaultWorkspaceName, defaultWorkspaceId } from '../../views/_pages/Work-space/constants'

export const useVerticalLayout = () => {
  const { roles } = useSelector((state) => state.userContextReducer.userContext)
  const userProfile = useSelector((state) => state.profileReducer.profile)
  const { appendToken } = useSasToken()
  const isAdmin = roles?.includes(Role.Admin)
  const isContributor = roles?.includes(Role.Contributor)
  // AIA-3454 IDH admin can access data marketplace admin
  const isIDHAdmin = userProfile?.isInvestmentDataHubAdmin
  const imgPath = `${staticAssetsPath}/images`
  const [hasWorkspaceContent, setHasWorkspaceContent] = useState(false)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const { isDarkTheme } = useThemeManager()
  const { workspaces } = useSelector((state) => state.workspaceReducer)
  const navigate = useNavigate()
  const { updateActiveWorkspace } = useWorkspaceService()

  React.useEffect(() => {
    if (workspaces && workspaces?.workspaceList?.length > 0) {
      setHasWorkspaceContent(true)
    } else {
      setHasWorkspaceContent(false)
    }
  }, [])

  // Split the already-open workspaces (from redux) into personal vs team so the
  // Workspace submenu can show them under separate categories. Team workspaces
  // carry a `privacy` of 'team' (or a `teamId`); everything else is personal.
  const openWorkspaces = workspaces?.workspaceList || []
  const isTeamWorkspace = (ws) => ws?.privacy === 'team' || ws?.teamId != null
  const personalOpenWorkspaces = openWorkspaces.filter((ws) => !isTeamWorkspace(ws))
  const teamOpenWorkspaces = openWorkspaces.filter(isTeamWorkspace)

  const toWorkspaceNavChild = (workspace) => ({
    id: workspace?.id,
    title: workspace?.name,
    icon: <WorkspaceIcon2 size={16} />,
    onClick: async () => {
      await updateActiveWorkspace(workspace?.id)
      navigate('/workspace')
    }
  })

  const aliIconPath = `${imgPath}/icons/${isDarkTheme ? 'ali_icon_dark.svg' : 'ali_icon.svg'}`
  const aliIconFallbackPath = `${imgPath}/icons/ali_icon.svg`

  const { value: centrali3_0 } = useFeatureGate('centrali3_0', false)
  const { value: canAccessReviewerAI } = useFeatureGate('canAccessReviewerAI', false)

  const verticalNavItems = [
    {
      header: (
        <div className="d-flex">
          <span>Smart Tools</span>
          <Badge className="ms-1" color="success" label="New" type="pill" />
        </div>
      )
    },
    {
      id: 'ali',
      title: 'Ali',
      icon: (
        <Icon
          id="ali"
          src={appendToken(aliIconPath)}
          width={35}
          onError={(e) => {
            e.currentTarget.onerror = null
            e.currentTarget.src = appendToken(aliIconFallbackPath)
          }}
        />
      ),
      navLink: '/chat',
      badge: 'light',
      badgetText: 'New'
    },
    {
      id: 'smartDocs',
      title: 'Docs',
      icon: (
        <Icon
          id="docs"
          src={appendToken(`${imgPath}/smarttools/smartdocs.png`)}
          width={30}
          height={30}
        />
      ),
      navLink: '/workspace?activeApp=Smart-docs'
    },
    {
      id: 'smartInsights',
      title: 'Insights',
      icon: (
        <Icon
          id="insights"
          src={appendToken(`${imgPath}/smarttools/insights.png`)}
          width={30}
          height={30}
        />
      ),
      navLink: '/workspace?activeApp=Smart-insights'
    },
    ...(canAccessReviewerAI
      ? [
          {
            id: 'rfpReviewer',
            title: 'RFP Reviewer',
            icon: (
              <Icon
                id="rfpReviewer"
                src={appendToken(`${imgPath}/logo/RFPReviewer.png`)}
                width={24}
                className="rounded-[5px]"
              />
            ),
            navLink: '/workspace?activeApp=RFP-Reviewer'
          }
        ]
      : []),
    {
      id: 'dataMarketplace',
      title: 'Data Marketplace',
      icon: <IconLayoutGrid size={20} color="var(--color-secondary)" />,
      navLink: '/data-marketplace'
    },
    {
      header: 'Apps and Workspace'
    },
    {
      id: 'dashboard',
      title: 'App store',
      icon: <IconHome size={20} />,
      badge: 'light',
      // badgeText: '2',
      navLink: '/app-store',
      tourClass: 'dashboard-tour'
    },
    {
      id: 'myspace',
      title: 'My space',
      icon: <IconBookmark size={20} color="var(--color-secondary)" />,
      navLink: '/myspace',
      tourClass: 'myspace-tour'
    },
    {
      id: 'allApps',
      title: 'All applications',
      icon: <IconStack2 size={20} />,
      navLink: '/all-applications'
    },
    {
      id: 'categories',
      title: 'Categories',
      icon: <IconBrandTrello size={20} />,
      navLink: '/categories',
      tourClass: 'categories-tour'
    },
    {
      id: 'collections',
      title: 'Collections',
      icon: <IconFolder size={20} />,
      navLink: '/collections',
      tourClass: 'collections-tour'
    },
    ...(centrali3_0
      ? [
          {
            id: 'teams',
            title: 'Teams',
            icon: <IconUsers size={20} />,
            navLink: '/teams'
          }
        ]
      : []),
    ...(isAdmin || isContributor || isIDHAdmin
      ? [
          {
            header: 'Admin Tools'
          },
          //removed for v1
          // {
          //   id: 'analytics',
          //   title: 'Usage analytics',
          //   icon: <PieChart size={20} />,
          //   navLink: '/analytics',
          //   tourClass: 'analytics-tour'
          // },
          ...(isAdmin || isContributor
            ? [
                {
                  id: 'applications',
                  title: 'Applications',
                  icon: <IconLayoutGrid size={20} />,
                  navLink: '/applications',
                  tourClass: 'analytics-tour'
                }
              ]
            : []),
          ...(isAdmin || isIDHAdmin
            ? [
                {
                  id: 'Data Marketplace',
                  title: 'Data Marketplace',
                  icon: <IconLayoutGrid size={20} color="var(--color-secondary)" />,
                  navLink: '/data-product-admin'
                }
              ]
            : []),
          ...(isAdmin || isContributor
            ? [
                {
                  id: 'notifications',
                  title: 'Notifications',
                  icon: <IconUsers size={20} />,
                  navLink: '/notification-groups'
                }
              ]
            : [])
        ]
      : [])
  ]

  const newVerticalNavItem = [
    {
      id: 'search',
      title: 'Search',
      icon: <IconSearch size={20} />,
      navLink: '/search',
      onClick: (e) => {
        setIsSearchModalOpen(true)
      }
    },
    {
      id: 'searchDivider',
      header: <div className="m-0 border-b border-[var(--frame-divider)]"></div>
    },
    {
      id: 'myspace',
      title: 'My space',
      icon: <HomeIcon size={20} />,
      navLink: '/my-space',
      tourClass: 'myspace-tour'
    },
    {
      id: 'ali',
      title: 'Ali',
      icon: (
        <Icon
          id="ali"
          src={appendToken(aliIconPath)}
          width={24}
          onError={(e) => {
            e.currentTarget.onerror = null
            e.currentTarget.src = appendToken(aliIconFallbackPath)
          }}
        />
      ),
      navLink: '/chat',
      badge: 'light',
      badgetText: 'New'
    },
    {
      id: 'aiApps',
      title: 'AI apps',
      icon: <AIApps size={20} />,
      children: [
        {
          header: 'APPLICATIONS'
        },
        {
          id: 'smartInsights',
          title: 'Smart Insights',
          icon: <SmartInsightsIcon size={20} />,
          navLink: '/workspace?activeApp=Smart-insights'
        },
        {
          id: 'smartDocs',
          title: 'Smart Docs',
          icon: <SmartDataIcon size={20} />,
          navLink: '/workspace?activeApp=Smart-docs'
        },
        ...(canAccessReviewerAI
          ? [
              {
                id: 'rfpReviewer',
                title: 'RFP Reviewer',
                icon: (
                  <Icon
                    id="rfpReviewer"
                    src={appendToken(`${imgPath}/logo/RFPReviewer.png`)}
                    width={24}
                    className="rounded-[5px]"
                  />
                ),
                navLink: '/workspace?activeApp=RFP-Reviewer'
              }
            ]
          : []),
        {
          header: 'AGENTS'
        },
        {
          id: 'manageAgents',
          title: 'AI Agents',
          icon: <ManageAgentIcon size={20} />,
          navLink: '/manage-agents'
        }
      ]
    },
    {
      id: 'workspace',
      title: 'Workspace',
      icon: <WorkspaceIcon2 size={20} />,
      navLink: '/workspace',
      children: [
        {
          header: 'My workspaces',
          navLink: '/workspace'
        },
        ...(hasWorkspaceContent
          ? personalOpenWorkspaces.slice(0, 5).map(toWorkspaceNavChild)
          : [
              {
                id: defaultWorkspaceId,
                title: defaultWorkspaceName,
                icon: <WorkspaceIcon2 size={16} />,
                onClick: async () => {
                  await updateActiveWorkspace(defaultWorkspaceId)
                  navigate('/workspace')
                }
              }
            ]),
        ...(teamOpenWorkspaces.length > 0
          ? [
              {
                header: 'Team workspaces'
              },
              ...teamOpenWorkspaces.slice(0, 5).map(toWorkspaceNavChild)
            ]
          : []),
        ...(hasWorkspaceContent
          ? [
              {
                header: (
                  <div className="d-flex justify-center align-items-center cursor-pointer border-t-[1px] theme-border-default pt-[8px]">
                    <button
                      className="card-header-text w-fit theme-surface text-primary rounded mt-[7px] hover:underline cursor-pointer transition-all duration-300 ease-in-out"
                      onClick={async () => {
                        await updateActiveWorkspace('my-workspace')
                        navigate('/workspace')
                      }}>
                      View all
                    </button>
                  </div>
                )
              }
            ]
          : [])
      ]
    },
    {
      id: 'teams',
      title: 'My teams',
      icon: <TeamsIcon size={20} />,
      navLink: '/teams'
    },
    {
      id: 'ParentMarketPlace',
      title: 'Marketplaces',
      icon: <IconLayoutGrid size={20} color="var(--text-heading)" />,
      children: [
        {
          id: 'dataMarketplaceChild',
          title: 'Data Marketplace',
          icon: <DataProductIcon size={20} color="var(--text-heading)" />,
          navLink: '/data-marketplace'
        },
        {
          id: 'appStoreChild',
          title: 'Application store',
          icon: <AllAppsIcon size={20} color="var(--text-heading)" />,
          navLink: '/application-store'
        },
        {
          id: 'aiMarketplaceChild',
          title: 'AI Marketplace',
          icon: <AIApps size={20} color="var(--text-heading)" />,
          navLink: '/ai-marketplace'
        }
      ]
    },
    ...(isAdmin || isContributor || isIDHAdmin
      ? [
          {
            id: 'adminApps',
            title: 'Admin center',
            icon: <AdminIcon />,
            children: [
              ...(isAdmin || isContributor
                ? [
                    {
                      id: 'applications',
                      title: 'Applications',
                      icon: <AllAppsIcon size={20} color="var(--text-heading)" />,
                      navLink: '/applications'
                    }
                  ]
                : []),
              ...(isAdmin || isIDHAdmin
                ? [
                    {
                      id: 'Data Marketplace',
                      title: 'Data Marketplace',
                      icon: <IconLayoutGrid size={20} color="var(--color-secondary)" />,
                      navLink: '/data-product-admin'
                    }
                  ]
                : []),
              ...(isAdmin || isContributor
                ? [
                    {
                      id: 'notifications',
                      title: 'Notifications',
                      icon: <IconBell size={20} color="var(--text-heading)" />,
                      navLink: '/notification-groups'
                    }
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      id: 'feature-gates',
                      title: 'Feature Gates',
                      icon: <IconFlag size={20} color="var(--text-heading)" />,
                      navLink: '/feature-gates'
                    },
                    {
                      id: 'banners',
                      title: 'Banners',
                      icon: <IconPhoto size={20} color="var(--text-heading)" />,
                      navLink: '/banners'
                    },
                    {
                      id: 'agents-skills',
                      title: 'Agents & Skills',
                      icon: <IconRobotFace size={20} color="var(--text-heading)" />,
                      navLink: '/agents-skills'
                    }
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      id: 'manage-mcp',
                      title: 'Manage ARC & MCP',
                      icon: <IconServer2 size={20} color="var(--text-heading)" />,
                      navLink: '/manage-mcp'
                    }
                  ]
                : [])
            ]
          }
        ]
      : [])
  ]
  return { verticalNavItems, newVerticalNavItem, isSearchModalOpen, setIsSearchModalOpen }
}
